import { Injectable, inject } from '@angular/core';
import { Observable, from, throwError } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import * as XLSX from 'xlsx';
import { HttpService } from '@core/services/http.service';
import { ReportDataParams } from './report-data.service';
import { Api } from '../../../../api/gse/api';
import {
  exportBackJobReport,
  ExportBackJobReport$Params,
} from '../../../../api/gse/fn/maintenance-report/export-back-job-report';
import {
  exportEquipmentServiceabilityReport,
  ExportEquipmentServiceabilityReport$Params,
} from '../../../../api/gse/fn/maintenance-report/export-equipment-serviceability-report';
import {
  exportPmComplianceReport,
  ExportPmComplianceReport$Params,
} from '../../../../api/gse/fn/maintenance-report/export-pm-compliance-report';
import {
  exportInventorySparePartReport,
  ExportInventorySparePartReport$Params,
} from '../../../../api/gse/fn/inventory-report/export-inventory-spare-part-report';
import {
  getInventoryMaintenanceHistoryReport,
  GetInventoryMaintenanceHistoryReport$Params,
} from '../../../../api/gse/fn/inventory-report/get-inventory-maintenance-history-report';
import { GetInventoryMaintenanceHistoryReportResponse } from '../../../../api/gse/models/get-inventory-maintenance-history-report-response';
import { InventoryMaintenanceHistoryReportDto } from '../../../../api/gse/models/inventory-maintenance-history-report-dto';
import {
  exportInventoryProcurementReport,
  ExportInventoryProcurementReport$Params,
} from '../../../../api/gse/fn/inventory-report/export-inventory-procurement-report';
import {
  exportTechnicianEfficiencyReport,
  ExportTechnicianEfficiencyReport$Params,
} from '../../../../api/gse/fn/maintenance-report/export-technician-efficiency-report';
import {
  exportJobCardTatReport,
  ExportJobCardTatReport$Params,
} from '../../../../api/gse/fn/maintenance-report/export-job-card-tat-report';
import {
  exportEquipmentDowntimeReport,
  ExportEquipmentDowntimeReport$Params,
} from '../../../../api/gse/fn/maintenance-report/export-equipment-downtime-report';
import { JobCardStatus } from '../../../../api/gse/models/job-card-status';
import { EquipmentType } from '../../../../api/gse/models/equipment-type';
import { EquipmentStatus } from '../../../../api/gse/models/equipment-status';
import { ServiceType } from '../../../../api/gse/models/service-type';
import { Priority } from '../../../../api/gse/models/priority';
import {
  exportEquipmentTatReport,
  ExportEquipmentTatReport$Params,
} from 'src/app/api/gse/functions';
import { getExportReportNameById } from '@features/gse/reports/config';
import { Api as AdministrationApi } from '../../../../api/administration/api';
import {
  exportDelegationReport,
  ExportDelegationReport$Params,
} from '../../../../api/administration/fn/delegation-report/export-delegation-report';
import { DelegationStatus } from '../../../../api/administration/models/delegation-status';
import {
  exportUsersReport,
  ExportUsersReport$Params,
} from '../../../../api/administration/fn/users-report/export-users-report';
import { UserStatus } from '../../../../api/administration/models/user-status';
import { ReportDataMapperService } from './report-data-mapper.service';

@Injectable({
  providedIn: 'root',
})
export class ReportExcelExportService {
  private httpService = inject(HttpService);
  private api = inject(Api);
  private administrationApi = inject(AdministrationApi);
  private dataMapper = inject(ReportDataMapperService);

  exportReport(reportType: string, params: ReportDataParams): Observable<void> {
    const exporter = this.getExporterForReport(reportType);
    if (!exporter) {
      return throwError(() => new Error(`No export implementation for report type: ${reportType}`));
    }

    return exporter(params).pipe(
      tap((blob: Blob) => {
        const timestamp = new Date().toISOString().slice(0, 10);
        const reportName = getExportReportNameById(reportType);
        this.downloadFile(blob, `${reportName}_${timestamp}.xlsx`);
      }),
      map(() => undefined as void)
    );
  }

  private getExporterForReport(
    reportType: string
  ): ((params: ReportDataParams) => Observable<Blob>) | undefined {
    const exporters: Record<string, (params: ReportDataParams) => Observable<Blob>> = {
      'back-job': (params) => this.exportBackJob(params),
      'equipment-serviceability': (params) => this.exportEquipmentServiceability(params),
      'pm-compliance': (params) => this.exportPmCompliance(params),
      'spare-parts': (params) => this.exportInventorySparePart(params),
      'maintenance-history': (params) => this.exportMaintenanceHistory(params),
      procurement: (params) => this.exportProcurement(params),
      'technician-efficiency': (params) => this.exportTechnicianEfficiency(params),
      'job-card-summary': (params) => this.exportJobCardSummary(params),
      'equipment-downtime': (params) => this.exportEquipmentDowntime(params),
      'equipment-tat': (params) => this.exportEquipmentTat(params),
      delegation: (params) => this.exportDelegation(params),
      'user-master-list': (params) => this.exportUserMasterList(params),
    };
    return exporters[reportType];
  }

  private exportBackJob(params: ReportDataParams): Observable<Blob> {
    const apiParams: ExportBackJobReport$Params = {
      startDate: params.startDate || '',
      endDate: params.endDate || '',
      sort: 'createdAt,desc',
    };

    if (params.search) apiParams.search = params.search;
    if (params.filters) {
      if (params.filters['equipment']) {
        const val = params.filters['equipment'];
        apiParams.equipmentIds = (Array.isArray(val) ? val : [val]).map((v) => Number(v));
      }
      if (params.filters['backJobStatus']) {
        const val = params.filters['backJobStatus'];
        apiParams.status = (Array.isArray(val) ? val : [val]) as JobCardStatus[];
      }
    }

    return this.api.invoke(exportBackJobReport, apiParams);
  }

  private exportEquipmentServiceability(params: ReportDataParams): Observable<Blob> {
    const apiParams: ExportEquipmentServiceabilityReport$Params = {
      search: params.search,
      startDate: params.startDate || '',
      endDate: params.endDate || '',
      sort: 'createdAt,desc',
    };

    if (params.filters) {
      if (params.filters['equipment']) {
        const val = params.filters['equipment'];
        apiParams.equipmentIds = (Array.isArray(val) ? val : [val]).map((v) => Number(v));
      }
      if (params.filters['equipmentCategory']) {
        const val = params.filters['equipmentCategory'];
        apiParams.categoryIds = (Array.isArray(val) ? val : [val]).map((v) => Number(v));
      }
      if (params.filters['equipmentType']) {
        const val = params.filters['equipmentType'];
        apiParams.type = (Array.isArray(val) ? val : [val]) as EquipmentType[];
      }
      if (params.filters['equipmentServiceability']) {
        const val = params.filters['equipmentServiceability'];
        apiParams.serviceability = (Array.isArray(val) ? val : [val]) as EquipmentStatus[];
      }
      if (params.filters['equipmentModel']) {
        const val = params.filters['equipmentModel'];
        apiParams.modelIds = (Array.isArray(val) ? val : [val]).map((v) => Number(v));
      }
    }

    return this.api.invoke(exportEquipmentServiceabilityReport, apiParams);
  }

  private exportPmCompliance(params: ReportDataParams): Observable<Blob> {
    const apiParams: ExportPmComplianceReport$Params = {
      startDate: params.startDate || '',
      endDate: params.endDate || '',
      sort: 'createdAt,desc',
    };

    if (params.search) apiParams.search = params.search;
    if (params.filters?.['equipment']) {
      const val = params.filters['equipment'];
      apiParams.equipmentIds = (Array.isArray(val) ? val : [val]).map((v) => Number(v));
    }

    return this.api.invoke(exportPmComplianceReport, apiParams);
  }

  private exportInventorySparePart(params: ReportDataParams): Observable<Blob> {
    const apiParams: ExportInventorySparePartReport$Params = {
      search: params.search,
      startDate: params.startDate || '',
      endDate: params.endDate || '',
      sort: 'createdAt,desc',
    };

    if (params.filters) {
      if (params.filters['sparePartCategory']) {
        const val = params.filters['sparePartCategory'];
        apiParams.categoryIds = (Array.isArray(val) ? val : [val]).map((v) => Number(v));
      }
      if (params.filters['sparePartBrand']) {
        const val = params.filters['sparePartBrand'];
        apiParams.brandIds = (Array.isArray(val) ? val : [val]).map((v) => Number(v));
      }
    }

    return this.api.invoke(exportInventorySparePartReport, apiParams);
  }

  private exportMaintenanceHistory(params: ReportDataParams): Observable<Blob> {
    const apiParams: GetInventoryMaintenanceHistoryReport$Params = {
      page: 0,
      size: params.size || 10000,
      sort: 'createdAt,desc',
      search: params.search,
      startDate: params.startDate,
      endDate: params.endDate,
    };

    if (params.filters) {
      if (params.filters['equipment']) {
        const val = params.filters['equipment'];
        apiParams.equipmentIds = (Array.isArray(val) ? val : [val]).map((v) => Number(v));
      }
      if (params.filters['technician']) {
        const val = params.filters['technician'];
        apiParams.technicianIds = (Array.isArray(val) ? val : [val]).map((v) => Number(v));
      }
      if (params.filters['maintenanceType']) {
        const val = params.filters['maintenanceType'];
        const selected = Array.isArray(val) ? val : [val];
        const preventiveTypes: ServiceType[] = [
          'PM_REGULAR',
          'PM_LOOP',
          'PM_SCHEDULED',
          'PM_TUV_RENEWAL',
          'PM_RAMP_PASS_RENEWAL',
          'PM_MAJOR',
          'PM_MINOR',
        ];
        const expanded: ServiceType[] = [];
        for (const v of selected) {
          if (v === 'PREVENTIVE_MAINTENANCE') {
            expanded.push(...preventiveTypes);
          } else {
            expanded.push(v as ServiceType);
          }
        }
        apiParams.maintenanceTypes = expanded;
      }
    }

    return from(this.api.invoke(getInventoryMaintenanceHistoryReport, apiParams)).pipe(
      map((response: GetInventoryMaintenanceHistoryReportResponse) => {
        const items = (response.data || []) as InventoryMaintenanceHistoryReportDto[];
        const rows = items.map((dto) => ({
          'Job Card ID': dto.jobCardId || '',
          'Start Date': dto.startDate || '',
          'End Date': dto.endDate || '',
          'Equipment Name': dto.equipmentName || '',
          'Maintenance Type': dto.maintenanceType || '',
          'Spare Parts': (dto.spareParts || []).join(', '),
          'Technicians': this.dataMapper.getTechnicianNamesByIds((dto.technicians || []).join(',')),
        }));
        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.json_to_sheet(rows);
        XLSX.utils.book_append_sheet(wb, ws, 'Maintenance History');
        const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        return new Blob([buf], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
      })
    );
  }

  private exportProcurement(params: ReportDataParams): Observable<Blob> {
    const apiParams: ExportInventoryProcurementReport$Params = {
      search: params.search,
      startDate: params.startDate || '',
      endDate: params.endDate || '',
      sort: 'createdAt,desc',
    };

    if (params.filters) {
      if (params.filters['pettyCash'] !== undefined) {
        const val = params.filters['pettyCash'];
        apiParams.pettyCashEnabled = Array.isArray(val)
          ? (val as string[]).includes('true')
          : val === 'true';
      }
    }

    return this.api.invoke(exportInventoryProcurementReport, apiParams);
  }

  private exportTechnicianEfficiency(params: ReportDataParams): Observable<Blob> {
    const apiParams: ExportTechnicianEfficiencyReport$Params = {
      startDate: params.startDate || '',
      endDate: params.endDate || '',
      sort: 'createdAt,desc',
    };

    if (params.search) apiParams.search = params.search;
    if (params.filters) {
      if (params.filters['technician']) {
        const val = params.filters['technician'];
        apiParams.technicianIds = (Array.isArray(val) ? val : [val]).map((v) => Number(v));
      }
    }

    return this.api.invoke(exportTechnicianEfficiencyReport, apiParams);
  }

  private exportJobCardSummary(params: ReportDataParams): Observable<Blob> {
    const apiParams: ExportJobCardTatReport$Params = {
      startDate: params.startDate || '',
      endDate: params.endDate || '',
      sort: 'createdAt,desc',
    };

    if (params.search) apiParams.search = params.search;
    if (params.filters) {
      if (params.filters['equipment']) {
        const val = params.filters['equipment'];
        apiParams.equipmentIds = (Array.isArray(val) ? val : [val]).map((v) => Number(v));
      }
      if (params.filters['jobCardStatus']) {
        const val = params.filters['jobCardStatus'];
        apiParams.status = String(Array.isArray(val) ? val[0] : val) as JobCardStatus;
      }
      if (params.filters['priority']) {
        const val = params.filters['priority'];
        apiParams.priority = String(Array.isArray(val) ? val[0] : val) as Priority;
      }
    }

    return this.api.invoke(exportJobCardTatReport, apiParams);
  }

  private exportEquipmentDowntime(params: ReportDataParams): Observable<Blob> {
    const apiParams: ExportEquipmentDowntimeReport$Params = {
      startDate: params.startDate || '',
      endDate: params.endDate || '',
      sort: 'createdAt,desc',
    };

    if (params.search) apiParams.search = params.search;
    if (params.filters) {
      if (params.filters['equipment']) {
        const val = params.filters['equipment'];
        apiParams.equipmentIds = (Array.isArray(val) ? val : [val]).map((v) => Number(v));
      }
    }

    return this.api.invoke(exportEquipmentDowntimeReport, apiParams);
  }

  private exportEquipmentTat(params: ReportDataParams): Observable<Blob> {
    const apiParams: ExportEquipmentTatReport$Params = {
      startDate: params.startDate || '',
      endDate: params.endDate || '',
      sort: 'createdAt,desc',
    };

    if (params.search) apiParams.search = params.search;
    if (params.filters) {
      if (params.filters['equipmentId']) {
        const val = params.filters['equipmentId'];
        apiParams.equipmentIds = (Array.isArray(val) ? val : [val]).map((v) => Number(v));
      }
      if (params.filters['manufacturer']) {
        const val = params.filters['manufacturer'];
        apiParams.manufacturerIds = (Array.isArray(val) ? val : [val]).map((v) => Number(v));
      }
      if (params.filters['equipmentModel']) {
        const val = params.filters['equipmentModel'];
        apiParams.modelIds = (Array.isArray(val) ? val : [val]).map((v) => Number(v));
      }
    }

    return this.api.invoke(exportEquipmentTatReport, apiParams);
  }

  private exportUserMasterList(params: ReportDataParams): Observable<Blob> {
    const apiParams: ExportUsersReport$Params = {
      sort: 'createdAt,desc',
    };

    if (params.search) apiParams.search = params.search;
    if (params.filters) {
      if (params.filters['userStatus']) {
        const val = params.filters['userStatus'];
        apiParams.status = (Array.isArray(val) ? val : [val]) as UserStatus[];
      }
    }

    return this.administrationApi.invoke(exportUsersReport, apiParams);
  }

  private exportDelegation(params: ReportDataParams): Observable<Blob> {
    const apiParams: ExportDelegationReport$Params = {
      startDate: params.startDate || '',
      endDate: params.endDate || '',
    };

    if (params.search) apiParams.search = params.search;
    if (params.filters) {
      if (params.filters['delegator']) {
        const val = params.filters['delegator'];
        apiParams.delegator = (Array.isArray(val) ? val : [val]).map((v) => Number(v));
      }
      if (params.filters['delegate']) {
        const val = params.filters['delegate'];
        apiParams.delegate = (Array.isArray(val) ? val : [val]).map((v) => Number(v));
      }
      if (params.filters['delegationStatus']) {
        const val = params.filters['delegationStatus'];
        apiParams.status = (Array.isArray(val) ? val : [val]) as DelegationStatus[];
      }
    }

    return this.administrationApi.invoke(exportDelegationReport, apiParams);
  }

  private downloadFile(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    window.URL.revokeObjectURL(url);
  }
}
